import type { BlockId, BlockType, ComponentsUnionType } from '@unifyapps/defs/types/block';
import _values from 'lodash/values';
import { useCallback, useMemo } from 'react';
import { useGlobalAppStateStore } from '../app/GlobalAppStateStore';
import { useInterfaceAppStore, useInterfaceStore } from './context';
import { useInterfaceStoreManager } from './runner/InterfaceStoreManagerContext';
import { getActivePage } from './runner/state';

export const useGetInterfaceStoreState = () => {
  return {
    getInterfaceStoreState: useInterfaceStore().getState,
  };
};

export const useGetInterfaceStoreStateByInstanceId = () => {
  const manager = useInterfaceStoreManager();
  return useCallback((instanceId?: string) => manager.getStore(instanceId).getState(), [manager]);
};

/**
 * Returns a function `(instanceId?) => activeInterfacePage` that resolves the active page
 * from the InterfaceStore scoped to the given instanceId. Use in propagated action handlers
 * where events/variables/functions must be resolved against the target instance's active page,
 * not the nearest `PageInstanceIdProvider`.
 */
export const useGetActivePageByInstanceId = () => {
  const getInterfaceStoreState = useGetInterfaceStoreStateByInstanceId();
  return useCallback(
    (instanceId?: string) => getActivePage(() => getInterfaceStoreState(instanceId)),
    [getInterfaceStoreState],
  );
};

export const useGetAppInterfaceStoreState = () => {
  return {
    getAppInterfaceStoreState: useInterfaceAppStore().getState,
  };
};

export const useBlocksInActiveInterfacePage = () => {
  const blocks = useBlocksFromPage();
  return useMemo(() => _values(blocks), [blocks]);
};

export const useBlockFromPage = <T = ComponentsUnionType>({ blockId }: { blockId: BlockId }) => {
  const baseBlock = useInterfaceStore().hooks.block.useFromBase({ blockId });
  const { device } = useGlobalAppStateStore().hooks.useDeviceDetails();
  const deviceBlock = useInterfaceStore().hooks.block.useFromDevice({ blockId, device });

  return (deviceBlock ?? baseBlock) as BlockType<T> | undefined;
};

export const useBlocksFromPage = () => {
  const baseBlocks = useInterfaceStore().hooks.blocks.useFromBaseDevice();
  const { device } = useGlobalAppStateStore().hooks.useDeviceDetails();
  const deviceBlocks = useInterfaceStore().hooks.blocks.useFromDevice({ deviceVariant: device });

  return useMemo(() => {
    // Most of the time deviceBlocks will be undefined, so we can return baseBlocks directly
    if (!deviceBlocks) {
      return baseBlocks ?? {};
    }

    return {
      ...baseBlocks,
      ...deviceBlocks,
    };
  }, [baseBlocks, deviceBlocks]);
};

/**
 * Include page variables from global page and active page
 * @returns all the page variables of the app
 */
export const useAppPageVariables = () => {
  const activePageVariables = useInterfaceStore().hooks.page.usePageVariables();
  const globalPageVariables = useInterfaceAppStore().hooks.page.usePageVariables();

  return useMemo(() => {
    return { ...activePageVariables, ...globalPageVariables };
  }, [activePageVariables, globalPageVariables]);
};

/**
 * Include page functions from global page and active page
 * @returns all the page functions of the app
 */
export const useAppPageFunctions = () => {
  const activePageFunctions = useInterfaceStore().hooks.page.usePageFunctions();
  const globalPageFunctions = useInterfaceAppStore().hooks.page.usePageFunctions();

  return useMemo(() => {
    return { ...activePageFunctions, ...globalPageFunctions };
  }, [activePageFunctions, globalPageFunctions]);
};

/**
 * Include folders from global page and active page
 * @returns all the folders of the app
 */
export const useAppFolders = () => {
  const activeFolders = useInterfaceStore().hooks.page.useFolders();
  const globalFolders = useInterfaceAppStore().hooks.page.useFolders();

  return useMemo(() => {
    return { ...activeFolders, ...globalFolders };
  }, [activeFolders, globalFolders]);
};

/**
 * Include blocks from global page and active page
 * @returns all the blocks of the app
 */
export const useAppBaseDeviceBlocks = () => {
  const activeBlocks = useInterfaceStore().hooks.blocks.useFromBaseDevice();
  const globalBlocks = useInterfaceAppStore().hooks.blocks.useFromBaseDevice();

  return useMemo(() => {
    return { ...activeBlocks, ...globalBlocks };
  }, [activeBlocks, globalBlocks]);
};

/**
 * Get all walkthrough steps from the active page
 * @returns all walkthrough steps from the active page as a record keyed by step id
 */
export const useAppWalkthroughSteps = () => {
  const steps = useInterfaceStore().hooks.walkthrough.useSteps();

  return useMemo(() => {
    if (!steps) return {};
    const stepsRecord: Record<string, (typeof steps)[0]> = {};
    steps.forEach((step) => {
      if (step?.id) {
        stepsRecord[step.id] = step;
      }
    });
    return stepsRecord;
  }, [steps]);
};
